package com.example.readingnotes.data.repository

import com.example.readingnotes.data.remote.api.ApiClient
import com.example.readingnotes.data.remote.dto.CreatePostRequestDto
import com.example.readingnotes.data.remote.dto.UpdatePostRequestDto
import com.example.readingnotes.data.remote.mapper.toDomain
import com.example.readingnotes.domain.model.Post
import com.example.readingnotes.domain.repository.PaginatedPosts
import com.example.readingnotes.domain.repository.PostRepository
import com.example.readingnotes.util.Result

class PostRepositoryImpl(
    private val apiClient: ApiClient,
) : PostRepository {
    override suspend fun getPosts(page: Int, pageSize: Int): Result<List<Post>> =
        try {
            val response = apiClient.getPosts(page, pageSize)
            Result.success(response.toDomain())
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun getPostsWithPagination(page: Int, pageSize: Int): Result<PaginatedPosts> =
        try {
            val response = apiClient.getPosts(page, pageSize)
            // For now, assume there are more pages if we receive a full page
            val hasMore = response.size >= pageSize
            Result.success(
                PaginatedPosts(
                    posts = response.toDomain(),
                    currentPage = page,
                    // Simplified - backend should return actual totalPages
                    totalPages = if (hasMore) page + 1 else page,
                    hasMore = hasMore,
                ),
            )
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun getPost(id: Int): Result<Post> =
        try {
            val response = apiClient.getPost(id)
            Result.success(response.toDomain())
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun createPost(
        title: String,
        contentMarkdown: String,
        imageUrl: String?,
        imageIds: List<Int>,
    ): Result<Post> =
        try {
            val response =
                apiClient.createPost(
                    CreatePostRequestDto(
                        title = title,
                        contentMarkdown = contentMarkdown,
                        imageUrl = imageUrl,
                        imageIds = imageIds,
                    ),
                )
            Result.success(response.toDomain())
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun updatePost(
        id: Int,
        title: String?,
        contentMarkdown: String?,
        imageUrl: String?,
        imageIds: List<Int>?,
        status: String?,
        totpCode: String?,
    ): Result<Post> =
        try {
            val response =
                apiClient.updatePost(
                    id,
                    UpdatePostRequestDto(
                        title = title,
                        contentMarkdown = contentMarkdown,
                        imageUrl = imageUrl,
                        imageIds = imageIds,
                        status = status,
                    ),
                    totpCode,
                )
            Result.success(response.toDomain())
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun deletePost(id: Int, totpCode: String?): Result<Unit> =
        try {
            apiClient.deletePost(id, totpCode)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun updatePostStatus(id: Int, status: String, totpCode: String?): Result<Post> =
        try {
            val response = apiClient.updatePostStatus(id, status, totpCode)
            Result.success(response.toDomain())
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun searchPosts(
        query: String,
        page: Int,
        pageSize: Int,
        status: String?,
    ): Result<com.example.readingnotes.domain.repository.SearchResult> =
        try {
            val response = apiClient.searchPosts(query, page, pageSize, status)
            Result.success(
                com.example.readingnotes.domain.repository.SearchResult(
                    results = response.results.map { it.toDomain() },
                    pagination =
                        com.example.readingnotes.domain.repository.SearchPagination(
                            page = response.pagination.page,
                            pageSize = response.pagination.pageSize,
                            total = response.pagination.total,
                            totalPages = response.pagination.totalPages,
                        ),
                    query = response.query,
                ),
            )
        } catch (e: Exception) {
            Result.failure(e)
        }
}
